<template>
  <ContentSection>
    <Container indent="lg-but-on-mobile-default">
      <div class="mb-8 max-w-container-text space-y-2 sm:mb-9">
        <!-- HEADER -->
        <h2
          v-if="data.heading"
          v-animate-in-from-bottom
          class="typo-heading-lg sm:typo-heading-xl"
          v-html="data.heading"
        />
        <!-- TEXT -->
        <div
          v-if="data.text"
          v-animate-in-from-bottom
          class="typo-body-xl sm:typo-body-2xl"
          v-html="data.text"
        />
      </div>

      <!-- FILTER -->
      <FilterChartDates
        v-if="isShowingFilter"
        v-model="filter"
        class="mb-3 sm:mb-5"
        :chart-data="props.data?.chart_data"
      />

      <div class="flex flex-col gap-x-8 2xl:gap-x-16 xl:flex-row">
        <!-- CHART -->
        <div
          v-if="data.chart_data"
          v-animate-in-from-bottom
          class="w-full max-w-container-text overflow-x-auto overflow-y-hidden"
        >
          <div class="aspect-video max-h-[80vh] w-full" :class="{ 'max-lg:min-w-[40rem]': amountDataRows < 100, 'max-lg:min-w-[60rem]': amountDataRows >= 100 && amountDataRows < 1000, 'max-xl:min-w-container-max': amountDataRows >= 1000 && amountDataRows < 3000, 'max-2xl:min-w-[150rem]': amountDataRows >= 3000 }">
            <VChart
              ref="chartElement"
              :style="{
                maxHeight: '80vh',
                width: '100%',
                height: '100%',
              }"
              :option="option"
            />
          </div>
        </div>

        <!-- LEGEND -->
        <ul
          v-if="data.chart_data && isShowingFilter"
          class="mt-3 flex flex-wrap gap-3 sm:mt-5 xl:w-64 xl:flex-col"
        >
          <li
            v-for="(serie, index) in series"
            :key="`${serie.name}-${index}`"
            class="h-fit max-sm:w-full xl:w-full"
          >
            <button
              type="button"
              class="w-full rounded-sm bg-grey-100 px-4 py-3 transition-colors typo-body-xl hover:bg-grey-200"

              @click="toggleSeries(index)"
              @mouseover="hoverSeries(index)"
              @mouseout="resetSeriesOpacity"
              @focus="hoverSeries(index)"
              @blur="resetSeriesOpacity"
            >
              <span
                class="flex items-center justify-between gap-5 transition-opacity"
                :class="{
                  'opacity-50': hiddenSeries.includes(index),
                }"
              >
                <!-- Title -->
                <span class="flex items-center gap-2">
                  <span
                    class="inline-block h-0 w-5 border-t-[3px]"
                    :class="[{
                      'border-dashed': serie.lineStyle.type === 'dashed',

                    }, { 'border-dotted': serie.lineStyle.type === 'dotted' }]"
                    :style="{ borderColor: serie.lineStyle.color }"
                  />
                  <span class="xl:min-w-16">{{ serie.name }}</span>
                </span>

                <!-- Yield -->
                <span
                  v-if="data.calc_yield"
                  class="typo-body-xl-fat"
                >
                  <span v-if="data.unit && data.unit_prefix">{{ data.unit }}</span>
                  <!-- This calculates the total yield for the series based on the filtered data (not the series data as they are already calculated) -->
                  <span>
                    {{ cumulativeYield(index) }}
                  </span>
                  <span v-if="data.unit && !data.unit_prefix">{{ data.unit }}</span>
                </span>
              </span>
            </button>
          </li>
        </ul>
      </div>

      <!-- LEGEND REGULAR -->
      <ChartLegend
        v-if="data.chart_data && !isShowingFilter"
        chart-type="line"
        :series="series"
        :hidden-series="hiddenSeries"
        @toggle-series="toggleSeries($event)"
        @hover-series="hoverSeries($event)"
        @reset-series-opacity="resetSeriesOpacity()"
      />
    </Container>
  </ContentSection>
</template>

<script lang="ts" setup>
import Decimal from 'decimal.js' // Import Decimal.js for precise calculations
import type { ComponentsMainChartLineFragment } from '#gql'

const props = defineProps<{
  data: Enforce<ComponentsMainChartLineFragment>
  prevComponent?: string | null
  nextComponent?: string | null
}>()

const chartElement = ref<null | HTMLDivElement>(null)

// Determine the last year with data available used for the initial filter range
const lastYearWithData = computed(() => {
  const chartData = props.data?.chart_data
  if (!Array.isArray(chartData) || chartData.length <= 1) {
    return null
  }
  const dates = chartData
    .slice(1) // Exclude header row
    .map(row => row.cells?.[0] ? new Date(row.cells[0]) : null)
    .filter(date => date && !isNaN(date.getTime()))
  const maxDate = dates.length ? new Date(Math.max(...dates.map(d => d!.getTime()))) : null
  if (maxDate) {
    return maxDate.getFullYear()
  }
  return parseInt(new Date().toISOString()
    .split('T')[0]) // Default to today if no data
})

/*
Define filter model and initialize with a default date range beginning of the year to today.
Edit: Added lastYearWithData to set the start date to the beginning of the last year with data.
EChart behaves unexpected if there are no data initially.
*/
const filter = ref<{
  start: Date | null
  end: Date
} | null>({
  start: (() => {
    const date = new Date()
    const year = lastYearWithData.value || date.getFullYear()
    date.setFullYear(year, 0, 1) // beginning of last year with data
    date.setHours(0, 0, 0, 0) // Normalize to midnight
    return date
  })(),
  end: (() => {
    const date = new Date()
    date.setHours(0, 0, 0, 0) // Normalize to midnight
    return date
  })(),
})

// Use localeProperties.iso if available, fallback to 'de-CH'
const { localeProperties } = useI18n()
const locale = localeProperties.value?.iso || 'de-CH'

const lineConfigs = computed(() => {
  return props.data.line_config
})

const isShowingFilter = computed(() => {
  return props.data.show_filter
})

const smooth = computed(() => {
  return props.data.line_smooth ? props.data.line_smooth / 100 : 0.2
})

const dataRowsFiltered = computed(() => {
  if (!Array.isArray(props.data.chart_data) || props.data.chart_data.length <= 1) {
    return []
  }

  const allDataRows = props.data.chart_data.slice(1) // Exclude the header row

  if (!isShowingFilter.value || !filter.value) {
    return allDataRows // Return all rows if filter is not shown or no filter is set
  }

  const dataRowsFiltered = allDataRows.filter((row) => {
    if (!row.cells?.[0]) return false
    const rowDate = new Date(row.cells[0])
    if (isNaN(rowDate.getTime())) {
      // If the date is invalid, skip this row
      return false
    }
    rowDate.setHours(0, 0, 0, 0) // Normalize to midnight for comparison
    return (
      !isNaN(rowDate.getTime())
      && (!filter.value.start || rowDate >= filter.value.start)
      && rowDate <= filter.value.end
    )
  })

  // If yield calculation is enabled, the data should start with zero. Therefore, we add a row which starts at zero.
  if (props.data.calc_yield && dataRowsFiltered.length > 0) {
    const firstRow = dataRowsFiltered[0]

    const newRow = {
      cells: ['', ...Array(firstRow.cells.length - 1).fill('0')],
    }

    dataRowsFiltered.unshift(newRow)
  }

  return dataRowsFiltered
})

const amountDataRows = computed(() => {
  return dataRowsFiltered.value?.length || 0
})

const xAxis = computed(() => {
  if (!Array.isArray(dataRowsFiltered.value) || !dataRowsFiltered.value.length) {
    return []
  }
  return dataRowsFiltered.value.map(item => item.cells[0])
})

// SERIES
const series = computed(() => {
  if (
    !Array.isArray(props.data.chart_data)
    || props.data.chart_data.length <= 1
  ) {
    return []
  }

  const headers = props.data.chart_data[0].cells.slice(1) // Extract headers excluding the first column
  // const dataRows = props.data.chart_data.slice(1) // Exclude the header row
  const dataRows = dataRowsFiltered.value // Use filtered data rows

  // Create a series for each column, excluding the first column
  return headers.map((header, index) => {
    const lines = {
      name: lineConfigs.value[index]?.title || header,
      type: 'line',
      smooth: smooth.value,
      data: mapDataRows(dataRows, index + 1), // +1 to skip the first column (date)
      lineStyle: {
        color: lineConfigs.value[index]?.line_color || 'auto',
        width: lineConfigs.value[index]?.line_width || 2,
        type: lineConfigs.value[index]?.line_type.value || 'solid',
      },
      showAllSymbol: true,
      showSymbol: true,
      symbol: 'circle',
      symbolSize: 7,
      itemStyle: {
        color: props.data.show_data_symbol ? lineConfigs.value[index]?.line_color : 'transparent',
      },
      emphasis: {
        focus: 'series', // Highlight entire series, fade others
      },
    }

    return lines
  })
})

// OPTIONS
const option = ref<ECOption>({
  textStyle: {
    fontFamily: 'Avenir Next LT Pro, Helvetica, Arial, sans-serif', // Set global font
  },

  grid: {
    left: 0,
    right: 35, // right padding
    top: 15, // top padding
    bottom: 20, // bottom padding -> will get unset if no filter is applied
    containLabel: true, // Ensure labels are contained within the grid
  },

  tooltip: {
    trigger: 'axis',
    backgroundColor: '#F3F7FF',
    borderWidth: 0,
    padding: [10, 12],
    extraCssText: 'border-radius: 0; box-shadow: none;',
    formatter: (params) => {
    // Handle both single params object and array of params for multiple series
      const paramsArray = Array.isArray(params) ? params : [params]
      const { name } = paramsArray[0] // Use the name from the first param (same for all series at this data point)
      const formattedName = useFormatDate(name, locale)

      // Build tooltip content for all series
      const seriesContent = paramsArray.map(({ seriesName, value, color, seriesIndex }) => {
        const lineStyle = series.value[seriesIndex]?.lineStyle || {
          type: 'solid',
          color,
        }
        const lineType = lineStyle.type || 'solid'
        const markerStyle = `
        display: inline-block;
        width: 15px;
        height: 0;
        margin-right: 8px;
        margin-bottom: 4px;
        border-bottom: 3px ${lineType} ${lineStyle.color || color};
        vertical-align: middle;
      `
        const marker = `<span style="${markerStyle}"></span>`

        // Format value: apply toFixed(2) only for floats with more than one decimal place
        let formattedValue
        if (typeof value === 'number' && !isNaN(value) && value % 1 !== 0) {
        // Convert to string to check decimal places
          const valueStr = value.toString()
          const decimalPart = valueStr.split('.')[1]
          // Apply toFixed(2) only if decimal part exists and is longer than 1
          formattedValue = decimalPart && decimalPart.length > 1 ? value.toFixed(2) : value
        } else {
        // Return strings or integers as-is
          formattedValue = value
        }

        formattedValue = useFormatNumber(formattedValue)

        // Apply unit formatting
        formattedValue = props.data.unit
          ? props.data.unit_prefix
            ? `${props.data.unit} ${formattedValue}`
            : `${formattedValue}${props.data.unit}`
          : formattedValue

        return `<span class="flex items-center justify-between gap-3"><span>${marker}${seriesName}:</span> <strong>${formattedValue}</strong></span>`
      }).join('')

      // If there is no name, do not show the tooltip (most likely the virtual zero row added for yield calculation)
      return name ? `<span class="typo-body-lg"><strong>${formattedName}</strong><br/>${seriesContent}</span>` : null
    },
  },

  xAxis: {
    type: 'category',
    data: xAxis.value,
    offset: 5,
    axisLabel: {
      fontSize: '16px', // typo-body-lg 0.875rem
      lineHeight: 20,
      fontWeight: 500,
      rotate: props.data.rotate_x_axis_labels ? 55 : 0,
      formatter: (value) => {
        return useFormatDate(value, locale, props.data?.date_format_x_axis?.value === 'short'
          ? {
              year: '2-digit',
              month: 'short',
            }
          : undefined)
      },

      margin: 10,
    },
    boundaryGap: !props.data.calc_yield,
  },

  yAxis: {
    type: 'value',
    // min: 'dataMin',
    // max: 'dataMax',
    axisLabel: {
      fontSize: '16px', // typo-body-lg 0.875rem
      lineHeight: 20,
      fontWeight: 500,
      padding: [0, 5, 0, 4], // Add padding to the left
      formatter: (value) => {
        const valueFormatted = useFormatNumber(value)

        if (!props.data.unit) return valueFormatted
        return props.data.unit_prefix
          ? `${props.data.unit} ${valueFormatted}`
          : `${valueFormatted}${props.data.unit}`
      },
    },
    splitLine: {
      show: true, // Enable the horizontal lines
      lineStyle: {
        color: '#CCD7EE', // Line color
        width: 1, // Line width (thin)
        type: 'solid', // Line style: 'solid', 'dashed', 'dotted', etc.
      },
    },
    // splitNumber: 10, // Number of splits on the y-axis
  },

  // Using now custom legend component
  // legend: {
  //   orient: 'horizontal',
  //   type: 'scroll', // Enable scrolling
  //   data: series.value.map(s => s?.name || 'undefined'), // Dynamically use series names
  //   left: 0,
  //   bottom: 0, // Position at bottom
  //   textStyle: { // typo-body-lg
  //     fontSize: '16px', // 14px 0.875rem
  //     lineHeight: 20,
  //     fontWeight: 500,
  //     padding: [0, 30, 5, 4],
  //   },
  //   itemGap: 0, // Space between legend items
  //   itemWidth: 35, // Marker width
  //   itemHeight: 3, // Marker height (matches 3px border-bottom)
  // },

  series: series.value,
})

const cumulativeYield = computed(() => {
  return (index: number) => {
    const product = dataRowsFiltered.value
      .map((row) => {
        const value = row.cells[index + 1]
        const parsedValue = value ? parseFloat(String(value).replace(/[\s ]/g, '').replace(',', '.')) : 0
        return new Decimal(1).plus(new Decimal(parsedValue).div(100))
      })
      .reduce((acc, val) => acc.mul(val), new Decimal(1))

    const cum = product.minus(1).mul(100)
    return cum.toFixed(2)
  }
})

// Update chart options when filter changes
watch(
  () => filter.value,
  () => {
    // Update chart options when filter changes
    option.value.xAxis.data = xAxis.value
    option.value.series = series.value
  },
)

function hoverSeries(index: number) {
  chartElement.value?.chart.dispatchAction({
    type: 'highlight',
    seriesIndex: index,
  })
}

function resetSeriesOpacity() {
  chartElement.value?.chart.dispatchAction({
    type: 'downplay',
  })
}

const hiddenSeries = ref<number[]>([])

function toggleSeries(index: number) {
  if (hiddenSeries.value.includes(index)) {
    hiddenSeries.value = hiddenSeries.value.filter(i => i !== index)
  } else {
    hiddenSeries.value.push(index)
  }

  chartElement.value?.chart.dispatchAction({
    type: 'legendToggleSelect',
    seriesIndex: index,
  })

  option.value.series = series.value.map((serie, i) => ({
    ...serie,
    data: hiddenSeries.value.includes(i) ? [] : serie.data,
  }))
}
// TODO
function mapDataRows(dataRows: any[], columnIndex: number) {
  if (!props.data.calc_yield) {
    return dataRows.map((row) => {
      const value = row.cells[columnIndex]
      // Clean the value: strip whitespace (Swiss thousand separator), replace comma with dot, remove % if present
      const cleanedValue = value ? String(value).replace(/[\s ]/g, '').replace(',', '.').replace('%', '') : '0'
      const parsedValue = new Decimal(cleanedValue).toNumber()
      return parsedValue
    })
  }

  // Calculate cumulative yield for each row
  return dataRows.map((row, index) => {
    // Multiply all factors (1 + value/100) up to the current row
    const cumulativeFactor = dataRows
      .slice(0, index + 1) // Include current row
      .reduce((acc, r) => {
        const value = r.cells[columnIndex]
        // Clean the value: strip whitespace (Swiss thousand separator), replace comma with dot, remove % if present
        const cleanedValue = value ? String(value).replace(/[\s ]/g, '').replace(',', '.').replace('%', '') : '0'
        const parsedValue = new Decimal(cleanedValue)
        const factor = new Decimal(1).plus(parsedValue.div(100))
        return acc.mul(factor)
      }, new Decimal(1))

    // Convert to cumulative yield percentage
    const cumulativeYield = cumulativeFactor.minus(1).mul(100)

    return cumulativeYield.toFixed(2)
  })
}

async function handleResize() {
  if (chartElement.value) {
    chartElement.value.resize() // Resize the chart instance
  }
}

const debouncedHandleResize = useDebounceFn(() => {
  handleResize()
}, 200)

onMounted(() => {
  window.addEventListener('resize', debouncedHandleResize)
})

onBeforeUnmount(() => {
  // Clean up event listener and chart instance
  window.removeEventListener('resize', debouncedHandleResize)
})
</script>
